import type {
  AccessGroup,
  RefusalKind,
  ServiceTeamRef,
} from "types/interfaces/access";
import {
  hiddenReason,
  hiddenText,
  marksOf,
  ruleOfGroup,
} from "components/userManagement/clientOwnershipModel";
import { ADMIN_ONLY_HINT } from "components/userManagement/form/lockHints";

/** Groups in picker order: the seeded order first, then by name. */
export function pickerOrder(groups: AccessGroup[]): AccessGroup[] {
  return [...groups].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
  );
}

export function groupName(
  groups: AccessGroup[] | undefined,
  id: number | null | undefined,
): string {
  if (id == null) return "No group";
  return groups?.find((g) => g.id === id)?.name ?? `Group #${id}`;
}

/** Why a member of `group` named on this client would not see it - their team
 * does not work it, or its demo mark is the wrong one for the group; null when
 * they would, or before a group is picked. */
export function assignedClientHidden(
  group: AccessGroup | null,
  client: { service_teams?: ServiceTeamRef[]; is_demo?: boolean } | undefined,
): string | null {
  if (!group || !client) return null;
  const rule = ruleOfGroup(group);
  const reason = hiddenReason(rule, marksOf(client));
  return reason ? hiddenText(reason, rule) : null;
}

/** What assigning clients means under `group`, for the Clients step. */
export function assignedClientsNote(group: AccessGroup | null): string | null {
  if (!group) return null;
  const scope = `${group.name} applies to: ${group.applies_to}.`;
  if (group.client_scope !== "assigned")
    return (
      `${scope} Assigned clients do not change what the user sees; they list ` +
      "the user as a Calibrate owner of the client."
    );
  const rules: string[] = [];
  if (group.team_name)
    rules.push(
      `Clients ${group.team_name} does not work stay out, assigned or not.`,
    );
  if (group.demo_clients === "exclude")
    rules.push("Demo clients stay out, assigned or not.");
  if (group.demo_clients === "only")
    rules.push("Only demo clients open, assigned or not.");
  return rules.length > 0 ? `${scope} ${rules.join(" ")}` : null;
}

/** One of a person's assigned clients, as the user form knows it: `client` when
 * the signed-in user reaches it, `outside` when the server says they do not. */
export interface AssignedClient {
  id: number;
  client?: {
    company_name: string;
    service_teams?: ServiceTeamRef[];
    is_demo?: boolean;
  };
  outside: boolean;
}

/** What the person will not see of their assigned clients once `group` is
 * theirs, said when the group is picked. A client the signed-in user does not
 * reach stays hidden under any group they may give, so it counts unnamed. */
export function hiddenAssignedNote(
  group: AccessGroup | null,
  person: string,
  assigned: AssignedClient[],
): string | null {
  if (!group || group.slug === "super_admin") return null;
  const named: string[] = [];
  let unnamed = 0;
  for (const row of assigned) {
    if (row.outside) {
      // Beyond the signed-in user's reach: hidden under every group they may
      // give. Named when the card carried its name.
      if (row.client) named.push(row.client.company_name);
      else unnamed += 1;
    } else if (row.client && assignedClientHidden(group, row.client)) {
      named.push(row.client.company_name);
    }
  }
  if (named.length === 0 && unnamed === 0) return null;
  const clients = (count: number) => (count === 1 ? "client" : "clients");
  let what: string;
  if (named.length === 0) what = `${unnamed} assigned ${clients(unnamed)}`;
  else if (unnamed === 0) what = listed(named);
  else
    what = `${listed(named)} and ${unnamed} more assigned ${clients(unnamed)}`;
  const rule = group.applies_to ? ` (Applies to: ${group.applies_to})` : "";
  return `${person} won't see ${what} as ${group.name}${rule}.`;
}

/** Why the signed-in user may not give a group here, as the Access step reads it. */
export interface GroupRefusal {
  kind: RefusalKind | null;
  /** The person's assigned clients the group would open beyond the signed-in
   * user's reach. */
  outside: number;
}

function listed(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** What a greyed group says when the pointer rests on it: a group past the
 * signed-in user's own access is a matter of rights; one that would show the
 * person assigned clients beyond theirs (`team` is their team), and the last
 * Super Admin's group, say their own reason. */
export function groupRefusalHint(
  group: AccessGroup,
  refusal: GroupRefusal,
  team: ServiceTeamRef | null | undefined,
  person: string,
): string {
  if (refusal.kind === "clients" && refusal.outside > 0) {
    const count = refusal.outside;
    const clients = count === 1 ? "client" : "clients";
    return (
      `${group.name} would also show ${person} ${count} assigned ${clients} ` +
      `outside ${team ? team.name : "the clients you see"}. Take ` +
      `${count === 1 ? "it" : "them"} off on the Clients step, or a Super ` +
      `Admin can assign ${group.name}.`
    );
  }
  if (refusal.kind === "last_super_admin") {
    return `${person} is the last active Super Admin and keeps that group.`;
  }
  return ADMIN_ONLY_HINT;
}
